import type { Rule, Filter } from "../../types/experiment";
import {
  ExperimentType,
  RuleSubtype,
  createDefaultFilter,
} from "../../types/experiment";
import { EnumSelect } from "../Shared/EnumSelect";
import { FilterEditor } from "./FilterEditor";
import { FilterPicker } from "./FilterPicker";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Trash2, GripVertical, Plus } from "lucide-react";

interface RuleEditorProps {
  rule: Rule;
  index: number;
  onChange: (rule: Rule) => void;
  onRemove: () => void;
}

const experimentTypeOptions = Object.entries(ExperimentType)
  .filter(([key]) => isNaN(Number(key)))
  .map(([key, value]) => ({ value: value as number, label: key }));

const ruleSubtypeOptions = Object.entries(RuleSubtype)
  .filter(([key]) => isNaN(Number(key)))
  .map(([key, value]) => ({ value: value as number, label: key }));

export function RuleEditor({ rule, index, onChange, onRemove }: RuleEditorProps) {
  const updateFilter = (filterIndex: number, filter: Filter) => {
    const filters = [...rule.filters];
    filters[filterIndex] = filter;
    onChange({ ...rule, filters });
  };

  const removeFilter = (filterIndex: number) => {
    onChange({ ...rule, filters: rule.filters.filter((_, i) => i !== filterIndex) });
  };

  const addFilter = (filterType: string) => {
    onChange({
      ...rule,
      filters: [...rule.filters, createDefaultFilter(filterType)],
    });
  };

  return (
    <div className="border-t border-surface1 pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-overlay0 cursor-grab" />
          <span className="text-xs font-semibold text-mauve">Rule {index + 1}</span>
          {rule.override && (
            <span className="text-[10px] text-overlay0 bg-surface0 px-1.5 py-0.5 rounded">
              override: V{rule.override.variation_id}
            </span>
          )}
        </div>
        <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-7 w-7">
          <Trash2 className="h-3.5 w-3.5 text-overlay1 hover:text-red" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <EnumSelect
          label="Type"
          value={rule.type}
          options={experimentTypeOptions}
          onChange={(value) => onChange({ ...rule, type: value })}
        />
        <EnumSelect
          label="Subtype"
          value={rule.subtype}
          options={ruleSubtypeOptions}
          onChange={(value) => onChange({ ...rule, subtype: value })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <Label className="mb-1.5 block text-xs text-overlay1">Hash</Label>
          <Input
            value={rule.hash}
            onChange={(e) => onChange({ ...rule, hash: e.target.value })}
            placeholder="rule hash"
          />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch
              checked={rule.is_sunset_rule}
              onCheckedChange={(checked) => onChange({ ...rule, is_sunset_rule: checked })}
            />
            <span className="text-xs text-subtext0">Sunset</span>
          </label>
          {!rule.override ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({ ...rule, override: { variation_id: 0 } })}
              className="text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Override
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Label className="text-[10px] text-overlay1 whitespace-nowrap">Override V</Label>
              <Input
                type="number"
                value={rule.override.variation_id}
                onChange={(e) =>
                  onChange({
                    ...rule,
                    override: { variation_id: parseInt(e.target.value) || 0 },
                  })
                }
                className="w-20 text-xs"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onChange({ ...rule, override: undefined })}
                className="h-7 w-7"
              >
                <Trash2 className="h-3 w-3 text-overlay1 hover:text-red" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-overlay1">
            {rule.filters.length} filter{rule.filters.length !== 1 ? "s" : ""}
          </span>
          <FilterPicker onSelect={addFilter} />
        </div>
        <div className="space-y-2">
          {rule.filters.map((filter, fi) => (
            <FilterEditor
              key={fi}
              filter={filter}
              onChange={(f) => updateFilter(fi, f)}
              onRemove={() => removeFilter(fi)}
            />
          ))}
          {rule.filters.length === 0 && (
            <div className="text-center py-4 text-xs text-overlay0 border border-dashed border-surface1 rounded-md">
              No filters - this rule applies to all users
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
